<script setup lang="ts">
/**
 * 应用外壳
 * 规格（Codex 交接 §3/§4）：
 *   桌面侧栏 224px / 顶栏 64px / 主内容 max 1200px
 *   小屏桌面侧栏 72px（图标 + tooltip）/ <768px 手机底部 Tab
 *   侧栏管「工作位置」；顶栏只在创作页提供创作类型切换
 */
import { computed, ref, onMounted, onBeforeUnmount, onUnmounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Icon from '@/components/Icon.vue';
import ComplianceFooter from '@/components/ComplianceFooter.vue';
import { api, getToken } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { useOperationStore } from '@/stores/operation';
/**
 * ★ 导航定义已抽到 `@/navigation`（单一真源，PC 侧栏 / 手机抽屉 / 「我的」页三处共用）。
 *   要加入口只改那一个文件 —— 别再在这里写第二份。
 */
import { NAV_GROUPS, SERVICE_NAV, drawerGroupsOf, MOBILE_TABS, type NavLeaf } from '@/navigation';

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const operation = useOperationStore();

const isMobile = ref(false);
const compact = ref(false);

function syncViewport() {
  const w = window.innerWidth;
  isMobile.value = w < 768;
  compact.value = w >= 768 && w < 1100;
}
onMounted(() => {
  syncViewport();
  window.addEventListener('resize', syncViewport);
});
onUnmounted(() => window.removeEventListener('resize', syncViewport));

/**
 * ★ 顶部提示条：改为「数据驱动 + 只在需要时出现」
 *
 *   原来这里是一句**写死的话**：
 *     「开发环境 · 对标/选题/脚本/积分已接真实服务；生成中心与作品库的成片链路尚未接通」
 *   它有三个问题叠在一起：
 *     ① **内容过期** —— 成片链路早就接通了（全链出片已实测通过），
 *        给用户看一句错话，比不显示更糟：他会以为功能没做好而不去用。
 *     ② **无条件显示** —— 连生产构建里都会挂着"开发环境"字样。
 *     ③ **手机上占 2 行** —— 390px 下这句长文案折成两行，
 *        白白吃掉首屏最值钱的位置。
 *
 *   现在改为从 `/production/capabilities` 读**真实状态**：
 *   后端已经会返回 `placeholderMedia`（有没有在用占位图）
 *   和 `note`（人话说明），前端不再自己编一句话 ——
 *   配好模型密钥后这条提示会自动消失，不需要有人记得回来改文案。
 */
const envNotice = ref<string | null>(null);
/** 是否已经探测过能力（避免每次路由切换都打一次接口） */
const capsProbed = ref(false);

/**
 * ★ 能力探测必须在**登录之后**做。
 *
 *   我第一版直接放在 onMounted 里，结果登录页也加载 AppShell，
 *   未登录就打了 `/production/capabilities` → **401**，
 *   浏览器控制台里多一条红色报错（H5 走查的 console 检查立刻抓到了）。
 *   所以改成 watch 登录态：登录后探一次，登出时清掉提示。
 *
 *   提示条**完全由数据决定，与环境无关**：
 *     placeholderMedia=true   → 正在用占位图，必须说
 *     两个出片通道都不可用    → 出不了片，必须说
 *     能力齐全                → 什么都不说（不打扰）
 *   我一开始写成"生产构建就不显示"，写完发现那更糟 ——
 *   当前部署恰恰就是占位图，生产环境里的用户才最需要这句说明。
 */
watch(
  () => auth.isLoggedIn,
  async (loggedIn) => {
    if (!loggedIn) {
      envNotice.value = null;
      capsProbed.value = false;
      return;
    }
    if (capsProbed.value) return;
    try {
      const caps = await api.productionCapabilities();
      capsProbed.value = true;
      if (caps.placeholderMedia) {
        envNotice.value = caps.note || '当前画面为占位图，配置模型密钥后为真实画面';
      } else if (!caps.slideshowReady && !caps.videoReady) {
        envNotice.value = '当前环境没有可用的出片通道，需配置模型密钥';
      } else {
        envNotice.value = null;
      }
    } catch {
      // 探测失败就静默不提示 —— 提示条不该因为探测失败而报错
    }
  },
  { immediate: true },
);

interface NavItem {
  key: string;
  label: string;
  icon: string;
  to: string;
  group?: string;
}

/**
 * ★ 主导航收敛为 **5 个入口**（2026-10-04 重构 P1）
 *
 * ── 为什么改 ──
 * 原来是 17 个平级入口铺满侧栏：用户找不到"我现在该去哪"，
 * 而且「对标分析」「选题与脚本」「生成中心」在用户心里其实是同一件事的不同阶段。
 *
 * ── 三条不能破的约束 ──
 *
 * ① **旧链接一律不能失效**（Codex 验收第 5 条）。
 *    下面每个子项都指向**原有路径**（`/business`、`/benchmark`、`/topics`、`/tasks`、
 *    `/works`、`/deliverables`、`/remix`、`/distributions`、`/metrics`、`/assets`、`/skills`）。
 *    本轮**只改分组，不改路径**，所以同事收藏的、客户收到的、文档里写的地址全都照旧可用。
 *    这也和既有先例一致：`/skills` 与 `/tools` 用 `alias` 而不是 `redirect`
 *    （`redirect` 会把 `?tool=xxx` 深链参数丢掉）。
 *
 * ② **子项文字尽量不改**（踩过的坑：`getByRole('button', {name:/写脚本/})` 这类
 *    文案定位器遍布既有用例，改一个词会连锁报红）。
 *    唯一一处有意的改动是 `/skills`：「工具」→「**工具（技能库）**」。
 *    理由：同一条路径有两种叫法（路由标题是「工具」，用户嘴里说的是「技能库」），
 *    而 `_e2e-bug12-narrow-nav.mjs` 一直在抽屉里找「技能库」——
 *    两个词都留着，两边都不破。
 *
 * ③ **手机抽屉仍要能一次看到全部入口**（子项不折叠），
 *    否则又会回到"手机上某个页面根本进不去"的老问题。
 */
/**
 * ★ 本轮把导航定义搬到了 `@/navigation`（单一真源）：PC 侧栏、手机抽屉、「我的」页三处共用，
 *   避免"改一处漏一处"。原来那份本地定义（NavLeaf/NavGroup/NAV_GROUPS/SERVICE_NAV/EXTRA_NAV）
 *   已删除，导入见文件顶部。
 */

/**
 * 手机抽屉：**按 5 个入口分组**，一次看全。
 *
 * ★ 为什么带分组标题、而不是纯扁平列表：
 *   纯扁平列表里"账号经营""发布与增长"这些**入口名根本不出现**（只有子项），
 *   手机上就看不到和桌面同一套心智模型；`_e2e-p1-nav.mjs` 也会（正确地）判它缺入口。
 *   所以抽屉 = 「5 个入口标题 + 各自子项」+ 「服务」组。
 *
 * ★ 仍然**不折叠**（子项全展开）：折叠能省地方，但会重新制造
 *   "手机上某个页面进不去"的老问题 —— 那是 BUG-12 的原始投诉。
 */
const drawerGroups = computed(() => drawerGroupsOf());

const moreItems = computed<NavLeaf[]>(() => drawerGroups.value.flatMap((g) => g.items));

const isActive = (to: string) => (to === '/' ? route.path === '/' : route.path.startsWith(to));

/** 当前所在的入口（用来决定哪个分组展开） */
const activeGroupKey = computed(() => {
  const p = route.path;
  if (p === '/') return 'workbench';
  const hit = NAV_GROUPS.find((g) => g.children.some((c) => isActive(c.to)));
  if (hit) return hit.key;
  const g = NAV_GROUPS.find((gr) => isActive(gr.to));
  return g?.key ?? '';
});

/**
 * 折叠状态：默认**展开当前所在组**，其余收起。
 *
 * 用户手动展开过的组记在 `openedGroups` 里；切页时重新按"当前组"展开，
 * 但不把用户手动打开的组关掉（正在对比两个模块时会很烦）。
 */
const openedGroups = ref<string[]>([]);
const isGroupOpen = (k: string) => openedGroups.value.includes(k);
function toggleGroup(k: string) {
  openedGroups.value = isGroupOpen(k) ? openedGroups.value.filter((x) => x !== k) : [...openedGroups.value, k];
}
watch(
  activeGroupKey,
  (k) => {
    if (k && !openedGroups.value.includes(k)) openedGroups.value = [...openedGroups.value, k];
  },
  { immediate: true },
);

/**
 * 手机底部 Tab（第一屏最常用的四个 + "更多"）。
 * ★ 第二个格子指向**账号经营**，第一屏就能看清"我在给哪个号做内容"。
 *   `/tasks`、`/works` 等仍然可达（侧栏「内容生产」与抽屉里都在）。
 */
/**
 * 手机底部 Tab —— **按已确认原型就是这 4 个**：工作台 / 内容工厂 / 发布与增长 / 我的。
 *
 * ★ 原来的第 5 个「更多」按钮**去掉了**：原型把"看全部入口"放进「我的」页
 *   （`/me` 里有一段「全部功能」，列的是同一份 NAV_GROUPS + 服务/额外入口）。
 *   这样保住 BUG-12 的原始诉求（手机上不能有页面进不去），同时底部只剩 4 个。
 *   `deploy/_e2e-p1-nav.mjs` 的 ⑥ 已同步改成"打开 /me 能看到全部入口"。
 */
const mobileTabs = MOBILE_TABS;

const moreOpen = ref(false);

/** 从抽屉里点走之后要把它关掉，否则会盖在目标页面上 */
watch(() => route.path, () => { moreOpen.value = false; });

const balanceText = computed(() =>
  auth.balance === null ? '—' : auth.balance.toLocaleString('zh-CN'),
);

/**
 * ★ 工单提醒（客户侧）
 *
 * ── 为什么要有 ──
 *
 * 我们给客户回了话，**客户不会收到任何通知** —— 他只能自己想起来再进来看一眼。
 * 工单的价值全在闭环，回了没被看到等于没回。
 *
 * ── 为什么是轮询，而不是"实时" ──
 *
 * 没有推送通道（没接短信/企微，也没有 websocket）。所以这里每 60 秒拉一次，
 * 并把这件事**如实写进 interaction 的语义里**：文案用「N 条工单等你回复」，
 * 不说"未读消息"—— 我们确实没做已读状态，客户点开看过、只是还没回，也算在内。
 *
 * 轮询只在登录后跑，并在卸载时清掉定时器（否则会留下一个永远在跑的请求）。
 */
const supportWaiting = ref(0);
let supportTimer: ReturnType<typeof setInterval> | null = null;

async function loadSupportSummary() {
  if (!getToken()) return;
  try {
    const s = await api.supportSummary();
    supportWaiting.value = s.waitingForUser ?? 0;
  } catch {
    /* 拿不到就不显示提醒，不能因此让整个外壳报错 */
  }
}

onMounted(() => {
  void loadSupportSummary();
  supportTimer = setInterval(() => void loadSupportSummary(), 60_000);
});

/**
 * ★ 经营账号列表：登录态就绪后拉一次（P1）
 *
 * `keep-if-owned` 而不是 `reset`：这是**页面刷新**场景，用户手动切过的账号
 * 只要仍属于本账号就保留（判据是服务端列表，不是 localStorage 里的字符串）。
 * 换号时由登录流程显式调 `reset`（见 auth store 的 syncBusiness 那套纪律）。
 */
watch(
  () => [auth.ready, auth.tenant?.id] as const,
  ([ready]) => {
    if (!ready) return;
    if (!getToken()) {
      operation.clear();
      return;
    }
    void operation.load('keep-if-owned');
  },
  { immediate: true },
);

/** 切换经营账号后跳到账号经营页做下一步 —— 只换上下文不跳页会让人以为没生效 */
function onAccountChange(id: string) {
  operation.select(id);
}
onBeforeUnmount(() => {
  if (supportTimer) clearInterval(supportTimer);
});

/** 进了工单页就顺手刷新一次（用户很可能刚回复完） */
watch(
  () => route.path,
  (p) => {
    if (p.startsWith('/support')) void loadSupportSummary();
    else void loadSupportSummary();
  },
);
</script>

<template>
  <div class="shell" :class="{ compact, mobile: isMobile }">
    <!-- ============ 侧栏（桌面） ============ -->
    <aside v-if="!isMobile" class="sidebar" aria-label="主导航">
      <div class="brand">
        <span class="mark" aria-hidden="true">获</span>
        <span v-if="!compact" class="brand-name">获客工厂</span>
      </div>

      <nav class="nav">
        <!--
          ★ 5 个入口（2026-10-04 重构 P1）
          有子项的入口是一个**可折叠分组**：点名字进它的门面页，点右侧箭头展开子项。
          折叠状态见 script 里的 openedGroups —— 默认展开"当前所在的那一组"，
          用户手动打开的组不会被自动关掉。
        -->
        <template v-for="g in NAV_GROUPS" :key="g.key">
          <div v-if="compact" class="nav-item-wrap">
            <RouterLink
              :to="g.to"
              class="nav-item"
              :class="{ on: activeGroupKey === g.key }"
              :title="g.label"
            >
              <Icon :name="g.icon" />
            </RouterLink>
          </div>

          <template v-else>
            <div class="nav-entry" :class="{ on: activeGroupKey === g.key }">
              <RouterLink :to="g.to" class="nav-item entry-main" :class="{ on: activeGroupKey === g.key }">
                <Icon :name="g.icon" />
                <span class="nav-label">{{ g.label }}</span>
              </RouterLink>
              <!-- 没有子项的入口（工作台）不显示箭头，免得点开是空的 -->
              <button
                v-if="g.children.length"
                type="button"
                class="entry-toggle"
                :aria-expanded="isGroupOpen(g.key)"
                :aria-label="`展开/收起「${g.label}」`"
                @click="toggleGroup(g.key)"
              >
                <Icon :name="isGroupOpen(g.key) ? 'chevronDown' : 'chevronRight'" :size="14" />
              </button>
            </div>

            <div v-if="g.children.length && isGroupOpen(g.key)" class="nav-children">
              <RouterLink
                v-for="c in g.children"
                :key="c.key"
                :to="c.to"
                class="nav-item child"
                :class="{ on: isActive(c.to) }"
              >
                <span class="child-dot" aria-hidden="true"></span>
                <span class="nav-label">{{ c.label }}</span>
              </RouterLink>
            </div>
          </template>
        </template>

        <div v-if="!compact" class="nav-group">服务</div>

        <RouterLink
          v-for="item in SERVICE_NAV"
          :key="item.key"
          :to="item.to"
          class="nav-item"
          :class="{ on: isActive(item.to) }"
          :title="compact ? item.label : undefined"
        >
          <Icon :name="item.icon" />
          <span v-if="!compact" class="nav-label">{{ item.label }}</span>
          <!-- 侧栏也带一个数字：用户在别的页面能看到"有工单在等我" -->
          <span v-if="item.key === 'support' && supportWaiting > 0" class="nav-badge num">
            {{ supportWaiting > 9 ? '9+' : supportWaiting }}
          </span>
        </RouterLink>
      </nav>

      <!--
        ★ 当前经营账号（P1）
        放在「当前项目」上面：先看清"我在给哪个号做内容"，再看"这个号卖什么"。
        切换后全局上下文（列表/Agent/新任务归属）都跟着走 —— store 里做了
        "属于自己才保留"的校正，避免上一个账号的选择残留。
      -->
      <div v-if="!compact" class="side-block account-block">
        <div class="side-label">当前经营账号</div>
        <div v-if="operation.accounts.length" class="acct-picker">
          <select
            class="acct-select"
            :value="operation.currentId ?? ''"
            aria-label="切换经营账号"
            @change="onAccountChange(($event.target as HTMLSelectElement).value)"
          >
            <option v-for="a in operation.accounts" :key="a.id" :value="a.id" :disabled="a.status === 'archived'">
              {{ a.name }}{{ a.status === 'paused' ? '（已暂停）' : '' }}{{ a.status === 'archived' ? '（已归档）' : '' }}
            </option>
          </select>
          <RouterLink to="/operation" class="acct-manage" title="管理经营账号">管理</RouterLink>
        </div>
        <RouterLink v-else-if="operation.isEmpty" to="/operation" class="project-link">
          <span class="dot warn" aria-hidden="true"></span>
          <span class="project-name">还没有经营账号</span>
        </RouterLink>
        <div v-else class="acct-loading">正在读取…</div>
      </div>

      <!-- 当前项目 -->
      <div v-if="!compact" class="side-block">
        <div class="side-label">当前项目</div>
        <RouterLink to="/business" class="project-link">
          <span class="dot" aria-hidden="true"></span>
          <span class="project-name">{{ auth.businessName || '尚未建档' }}</span>
        </RouterLink>
      </div>

      <!-- 服务顾问（FDE 对外称「增长陪跑服务」） -->
      <div v-if="!compact" class="side-block advisor">
        <div class="advisor-title">我的服务顾问</div>
        <p class="advisor-desc">从内容定位到持续生产</p>
        <RouterLink to="/advisor" class="btn btn-ghost advisor-btn">了解陪跑服务</RouterLink>
      </div>

      <div v-if="!compact" class="side-user">
        <span class="avatar" aria-hidden="true">{{ (auth.nickname || '用')[0] }}</span>
        <span class="user-meta">
          <span class="user-name">{{ auth.nickname || '未登录' }}</span>
          <span class="user-org">{{ auth.tenantName || '企业工作空间' }}</span>
        </span>
      </div>
    </aside>

    <!-- ============ 主区 ============ -->
    <main class="main">
      <!--
        环境提示条：只在"能力不完整"时出现（见 script 里 envNotice 的说明）。
        手机上强制单行 + 省略号 —— 390px 里两行提示会吃掉首屏最值钱的位置。
      -->
      <div v-if="envNotice" class="demo-notice" role="note" :title="envNotice">
        {{ envNotice }}
      </div>

      <header class="topbar">
        <!--
          ★ 面包屑：现在带上「经营账号」
          手机上侧栏是隐藏的，所以手机端单独显示一个可点的账号胶囊（见下），
          保证"我现在在哪个经营账号里"在任何尺寸下都看得见（验收第 3 条）。
        -->
        <div class="crumb">
          <span class="crumb-page">{{ route.meta.title ?? '工作台' }}</span>
          <template v-if="operation.currentName">
            <span class="crumb-sep">/</span>
            <span class="crumb-acct" :title="`当前经营账号：${operation.currentName}`">
              {{ operation.currentName }}
            </span>
          </template>
          <template v-if="auth.businessName">
            <span class="crumb-sep">/</span>
            <span class="crumb-project">{{ auth.businessName }}</span>
          </template>
        </div>
        <!-- 手机端：账号切换胶囊（侧栏在手机上不可见，这里必须能切） -->
        <RouterLink
          v-if="isMobile && operation.accounts.length"
          to="/operation"
          class="acct-chip"
          :title="`当前经营账号：${operation.currentName ?? ''}`"
        >
          <Icon name="target" :size="14" />
          <span class="acct-chip-name">{{ operation.currentName }}</span>
        </RouterLink>
        <div class="topbar-actions">
          <!--
            ★ 手机端「全部入口」按钮（2026-10-05 新版原型）：
            底部 Tab 按原型只留 4 个（工作台/内容工厂/发布与增长/我的），
            原来第 5 个「更多」Tab 去掉之后，抽屉就没有入口了 ——
            这里把入口挪到顶栏，保住 BUG-12 的原始诉求：**手机上任何页面都进得去**。
          -->
          <button
            v-if="isMobile"
            type="button"
            class="icon-btn all-entries"
            :aria-expanded="moreOpen"
            aria-label="全部入口"
            title="全部入口"
            @click="moreOpen = !moreOpen"
          >
            <Icon name="menu" :size="18" />
          </button>
          <RouterLink to="/credits" class="pill coin">
            <Icon name="coins" :size="16" />
            <span class="num">{{ balanceText }}</span>
          </RouterLink>
          <RouterLink to="/advisor" class="pill">增长陪跑服务</RouterLink>
          <!--
            ★ 工单提醒
            有"等客户回复"的工单时才出现红点 —— 没有就不占位置、也不制造假的紧迫感。
            文案用 title 说明白："N 条工单等你回复"（不是"未读"，我们没做已读状态）。
          -->
          <RouterLink
            v-if="supportWaiting > 0"
            to="/support"
            class="icon-btn support-bell"
            :aria-label="`${supportWaiting} 条工单等你回复`"
            :title="`${supportWaiting} 条工单等你回复`"
          >
            <Icon name="bell" :size="18" />
            <span class="dot num">{{ supportWaiting > 9 ? '9+' : supportWaiting }}</span>
          </RouterLink>
          <!--
            BUG-17：原来这里是个 <button>，**没绑任何点击事件** ——
            用户点了完全没反应，比没有入口更糟（会以为系统坏了）。
            改成指向真实的 /help 页面。
          -->
          <RouterLink to="/help" class="icon-btn" aria-label="帮助与规范">
            <Icon name="help" :size="18" />
          </RouterLink>
        </div>
      </header>

      <div class="scroll">
        <div class="content">
          <!--
            ★ 等会话就绪再挂载子页面（见 App.vue 里的说明）。
            不等的话，每个"进门就按 auth.businessId 拉数据"的页面都会先拉一次空 ——
            典型症状：新会话第一次进「选题与脚本」显示「还没有选题」，而选题是有的。
            代价只是一个很短的占位，换来所有页面都不用各自处理"档案还没到"。
          -->
          <RouterView v-if="auth.ready" />
          <div v-else class="boot-hold" role="status" aria-live="polite">
            <span class="boot-dot"></span>
            <span>正在加载你的工作台…</span>
          </div>
        </div>
        <!-- 合规公示（备案号等）—— 登录后的页面同样要能看到 -->
        <ComplianceFooter />
      </div>
    </main>

    <!-- ============ 底部 Tab（手机） ============ -->
    <nav v-if="isMobile" class="mobile-tabs" aria-label="手机导航">
      <template v-for="t in mobileTabs" :key="t.key">
        <button
          v-if="t.key === 'more'"
          type="button"
          class="tab"
          :aria-expanded="moreOpen"
          aria-label="更多入口"
          @click="moreOpen = !moreOpen"
        >
          <Icon :name="t.icon" :size="22" />
          <span class="tab-label">{{ t.label }}</span>
        </button>
        <RouterLink v-else :to="t.to" class="tab" :class="{ on: isActive(t.to) }">
          <Icon :name="t.icon" :size="22" />
          <span class="tab-label">{{ t.label }}</span>
        </RouterLink>
      </template>
    </nav>

    <!--
      BUG-12：窄屏「更多」抽屉。
      改之前技能库/对标分析/素材与角色/智能体在手机上**根本进不去**
      （侧栏 v-if="!isMobile" 隐藏，底部只有 4 个 tab）。
      对标分析还是核心流程的一环。
    -->
    <div v-if="isMobile && moreOpen" class="sheet-mask" @click.self="moreOpen = false">
      <div class="sheet" role="dialog" aria-label="全部入口">
        <div class="sheet-head">
          <span>全部入口</span>
          <button type="button" class="sheet-x" aria-label="关闭" @click="moreOpen = false">
            <Icon name="x" :size="18" />
          </button>
        </div>
        <template v-for="g in drawerGroups" :key="g.key">
          <div class="sheet-group">{{ g.label }}</div>
          <RouterLink v-for="m in g.items" :key="m.key" :to="m.to" class="sheet-item">
            <Icon :name="m.icon" :size="18" />
            <span>{{ m.label }}</span>
            <Icon name="chevronRight" :size="16" class="sheet-arrow" />
          </RouterLink>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.shell {
  display: flex;
  min-height: 100vh;
  background: var(--bg-base);
}

/* ---------- 侧栏 ---------- */
.sidebar {
  width: var(--sidebar-w);
  flex-shrink: 0;
  background: var(--bg-subtle);
  border-right: 1px solid var(--line);
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  height: 100vh;
  padding: var(--s-3) 0;
}
.compact .sidebar {
  width: var(--sidebar-w-compact);
  align-items: center;
}

.brand {
  display: flex;
  align-items: center;
  gap: var(--s-3);
  padding: var(--s-2) var(--s-5) var(--s-5);
}
.mark {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: var(--bg-inverse);
  color: #fff;
  display: grid;
  place-items: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
}
.brand-name {
  font-size: 15px;
  font-weight: var(--fw-semibold);
  letter-spacing: -0.01em;
}

.nav {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 0 var(--s-3);
  width: 100%;
}
.compact .nav {
  padding: 0;
  align-items: center;
}
.nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 38px;
  padding: 0 var(--s-3);
  border-radius: var(--r-btn);
  color: var(--text-2);
  font-size: var(--fs-body);
  transition:
    background var(--t-fast) var(--ease),
    color var(--t-fast) var(--ease);
}
.compact .nav-item {
  width: 40px;
  justify-content: center;
  padding: 0;
}
.nav-item:hover {
  background: var(--bg-muted);
  color: var(--text-1);
}
.nav-item.on {
  background: var(--bg-base);
  color: var(--accent);
  font-weight: 500;
  box-shadow: inset 0 0 0 1px var(--line);
}
.nav-item .icon {
  color: currentColor;
}

/* ---------- 5 入口分组（P1） ---------- */
/* 入口行 = 「名字（可点，进门面页）」+「箭头（可点，展开子项）」两段式 */
.nav-entry {
  display: flex;
  align-items: center;
  border-radius: var(--r-btn);
}
.nav-entry:hover {
  background: var(--bg-muted);
}
.nav-entry.on {
  background: var(--bg-base);
  box-shadow: inset 0 0 0 1px var(--line);
}
/* 分组行自己已经有底色，里面的 nav-item 不要再叠一层 hover/选中底色 */
.nav-entry .entry-main {
  flex: 1;
  min-width: 0;
}
.nav-entry:hover .entry-main,
.nav-entry .entry-main:hover {
  background: transparent;
  box-shadow: none;
}
.nav-entry .entry-main.on {
  background: transparent;
  box-shadow: none;
}
.entry-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 38px;
  border: 0;
  background: transparent;
  color: var(--text-4);
  cursor: pointer;
  border-radius: var(--r-btn);
}
.entry-toggle:hover {
  color: var(--text-1);
}
/* 缩进一条细线，让「子项属于上面那个入口」一眼看得出来 */
.nav-children {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin: 1px 0 2px;
  padding-left: 14px;
  border-left: 1px solid var(--line);
  margin-left: 16px;
}
.nav-item.child {
  height: 32px;
  font-size: 13px;
  padding-left: var(--s-2);
}
.nav-item.child.on {
  background: transparent;
  box-shadow: none;
  color: var(--accent);
  font-weight: 500;
}
.child-dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.5;
  flex: none;
}
.nav-item.child.on .child-dot {
  opacity: 1;
}

.nav-group {
  padding: var(--s-5) var(--s-3) var(--s-2);
  font-size: var(--fs-caption);
  color: var(--text-4);
}

/* ---------- 侧栏区块 ---------- */
.side-block {
  padding: var(--s-5) var(--s-5) 0;
}
.side-label {
  font-size: var(--fs-caption);
  color: var(--text-4);
  margin-bottom: var(--s-2);
}
.project-link {
  display: flex;
  align-items: center;
  gap: var(--s-2);
  font-size: 13px;
  color: var(--text-2);
}
.project-link:hover {
  color: var(--text-1);
}
.project-link .dot {
  width: 8px;
  height: 8px;
  border-radius: 3px;
  background: var(--accent);
  flex-shrink: 0;
}
/* 没有经营账号时那个点用告警色 —— 状态要一眼能分辨，不要靠读文字 */
.project-link .dot.warn {
  background: var(--warn, #d97706);
}
.project-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ---------- 当前经营账号选择器（P1） ---------- */
.account-block .acct-picker {
  display: flex;
  align-items: center;
  gap: var(--s-2);
}
.acct-select {
  flex: 1;
  min-width: 0;
  height: 32px;
  padding: 0 var(--s-2);
  border: 1px solid var(--line);
  border-radius: var(--r-btn);
  background: var(--bg-base);
  color: var(--text-1);
  font-size: 13px;
  cursor: pointer;
}
.acct-select:hover {
  border-color: var(--accent);
}
.acct-manage {
  font-size: var(--fs-caption);
  color: var(--text-3);
  flex: none;
}
.acct-manage:hover {
  color: var(--accent);
}
.acct-loading {
  font-size: 13px;
  color: var(--text-4);
}
/* 面包屑里的经营账号：用弱一点的颜色，别和页面标题抢 */
.crumb-acct {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* 手机端账号胶囊：侧栏在手机上不可见，这个必须能点到 */
.acct-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid var(--line);
  border-radius: 999px;
  font-size: 13px;
  color: var(--text-2);
  background: var(--bg-base);
  max-width: 45vw;
  /**
   * ★ `min-width: 0` 不能省（2026-10-05 真机量出来的）
   *
   * topbar 的 **min-content 宽度** = padding(32) + 胶囊(45vw) + gap(16) + 动作区(198，硬币+铃+帮助)：
   *   · 320px 视口 → 32+144+16+198 = **390** ⇒ 整个文档被撑到 390，**横向滚 70px**
   *   · 430px 视口 → 32+193+16+198 = **439** ⇒ 横向滚 9px
   * 也就是说：在一个 320px 的手机上，**整个 app 都能左右滑**，而罪魁是这个胶囊
   * （它名字里有 `overflow:hidden` + 省略号，但 flex 项的**自动最小尺寸**仍按内容算 ——
   *  `min-width:0` 才让省略号真正生效）。
   * 压到最小后：32+40+16+198 = 286 ≤ 320，名字超出部分走省略号，title 里仍有全名。
   */
  min-width: 0;
}
.acct-chip-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.acct-chip:hover {
  color: var(--accent);
  border-color: var(--accent);
}

.advisor {
  margin-top: auto;
  padding-top: var(--s-6);
}
.advisor-title {
  font-size: 13px;
  font-weight: var(--fw-semibold);
  margin-bottom: 2px;
}
.advisor-desc {
  font-size: var(--fs-caption);
  color: var(--text-3);
  margin-bottom: var(--s-3);
  line-height: 1.45;
}
.advisor-btn {
  width: 100%;
  height: 32px;
  font-size: 13px;
}

.side-user {
  display: flex;
  align-items: center;
  gap: var(--s-3);
  padding: var(--s-5) var(--s-5) var(--s-2);
  border-top: 1px solid var(--line);
  margin-top: var(--s-5);
}
.avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--bg-muted);
  color: var(--text-2);
  display: grid;
  place-items: center;
  font-size: 12px;
  font-weight: var(--fw-semibold);
  flex-shrink: 0;
}
.user-meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.user-name {
  font-size: 13px;
  font-weight: 500;
}
.user-org {
  font-size: 11px;
  color: var(--text-3);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ---------- 主区 ---------- */
.main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.demo-notice {
  background: var(--bg-muted);
  color: var(--text-3);
  font-size: 12px;
  text-align: center;
  padding: 6px var(--s-4);
  border-bottom: 1px solid var(--line);
  /* 长文案在窄屏必须截断成一行，否则会折成两三行白占首屏 */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.topbar {
  height: var(--topbar-h);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: var(--s-4);
  padding: 0 var(--s-8);
  border-bottom: 1px solid var(--line);
  background: var(--bg-base);
  position: sticky;
  top: 0;
  z-index: var(--z-sticky);
}
.mobile .topbar {
  padding: 0 var(--s-4);
}
.crumb {
  display: flex;
  align-items: center;
  gap: var(--s-2);
  font-size: var(--fs-body);
  flex: 1;
  min-width: 0;
}
.crumb-page {
  color: var(--text-1);
  font-weight: 500;
  /* 页面名不能被折成两行（手机 390px 上出现过"创作工作/台"） */
  white-space: nowrap;
  flex-shrink: 0;
}
.crumb-sep,
.crumb-project {
  color: var(--text-3);
}
.crumb-project {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.topbar-actions {
  display: flex;
  align-items: center;
  gap: var(--s-2);
}
.pill.coin {
  font-variant-numeric: tabular-nums;
  color: var(--text-1);
  font-weight: 500;
}
.icon-btn {
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border-radius: var(--r-btn);
  color: var(--text-3);
}
.icon-btn:hover {
  background: var(--bg-muted);
  color: var(--text-1);
}
/* ---------- 工单提醒 ---------- */
.support-bell {
  position: relative;
}
.support-bell .dot {
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: var(--danger, #dc2626);
  color: #fff;
  font-size: 10px;
  line-height: 16px;
  text-align: center;
  font-weight: 600;
}
/* 侧栏那一项上的数字 */
.nav-badge {
  margin-left: auto;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--danger, #dc2626);
  color: #fff;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
  font-weight: 600;
}
.mobile .topbar-actions .pill:not(.coin) {
  display: none;
}

.scroll {
  flex: 1;
  overflow-y: auto;
}
.content {
  max-width: var(--content-max);
  margin: 0 auto;
  padding: var(--s-8) var(--s-8) var(--s-16);
}
/* 会话就绪前的占位：几行灰条，别让页面在"空"和"有数据"之间闪 */
.boot-hold {
  display: flex;
  align-items: center;
  gap: var(--s-3);
  padding: var(--s-10) 0;
  color: var(--text-3);
  font-size: var(--fs-body);
}
.boot-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--accent);
  animation: boot-pulse 1.1s ease-in-out infinite;
}
@keyframes boot-pulse {
  0%,
  100% {
    opacity: 0.25;
    transform: scale(0.85);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
}
.mobile .content {
  padding: var(--s-5) var(--s-4) 96px; /* 给底部 tab 留位 */
}

/* ---------- BUG-12：窄屏「更多」抽屉 ----------
   改之前技能库/对标分析/素材与角色/智能体在手机上**根本进不去**
   （侧栏 v-if="!isMobile" 隐藏，底部只有 4 个 tab）。 */
.sheet-mask {
  position: fixed;
  inset: 0;
  /*
   * ★ 必须用项目既有的层级阶梯，不要自己写魔数。
   *   我第一版随手写了 60，而 topbar 是 --z-sticky(100) ——
   *   结果遮罩被压在顶栏下面，屏幕上半部分点不到遮罩、
   *   会误触到后面的页面（验收点遮罩时被 topbar 拦截，才发现）。
   *   --z-modal(300) 高于 sticky(100) 与 dropdown(200)，是模态该用的档。
   */
  z-index: var(--z-modal);
  background: rgba(15, 23, 42, 0.35);
  display: flex;
  align-items: flex-end;
}
.sheet {
  width: 100%;
  max-height: 72vh;
  overflow: auto;
  background: var(--bg, #fff);
  border-radius: 14px 14px 0 0;
  padding: var(--s-3) var(--s-3) calc(var(--s-5) + env(safe-area-inset-bottom, 0px));
}
.sheet-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 4px 10px;
  color: var(--text-3);
  font-size: 13px;
}
.sheet-x {
  background: none;
  border: 0;
  color: var(--text-3);
  padding: 6px;
  cursor: pointer;
}
/* 抽屉里的分组标题：和桌面侧栏同一套心智模型（5 个入口 + 服务） */
.sheet-group {
  padding: 14px 6px 4px;
  font-size: var(--fs-caption);
  color: var(--text-4);
}
.sheet-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 13px 6px;
  color: var(--text-1);
  text-decoration: none;
  border-top: 1px solid var(--line);
}
.sheet-item .sheet-arrow {
  margin-left: auto;
  color: var(--text-3);
}

/* ---------- 底部 Tab ---------- */
.mobile-tabs {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: var(--z-sticky);
  display: flex;
  background: var(--bg-base);
  border-top: 1px solid var(--line);
  padding-bottom: env(safe-area-inset-bottom, 0);
}
.tab {
  flex: 1;
  min-height: 52px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  color: var(--text-3);
  font-size: 11px;
}
.tab.on {
  color: var(--accent);
}
.tab-label {
  line-height: 1;
}
</style>
